import React, { createContext, useContext, useEffect, useState } from "react";
import { initializeApp } from "firebase/app";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  onAuthStateChanged,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  type User,
  type Auth,
} from "firebase/auth";
import { setAuthEmailForApi } from "../lib/authEmail";
import { sendFunnelEvent } from "../lib/funnel";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase conditionally
let app: ReturnType<typeof initializeApp> | undefined;
let auth: Auth | undefined;
let googleProvider: GoogleAuthProvider | undefined;
const LOGIN_EVENT_UID_KEY = "IW_LAST_LOGIN_EVENT_UID";
const EMAIL_LINK_KEY = "IW_EMAIL_LINK_PENDING";

try {
  if (firebaseConfig.apiKey) {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
    googleProvider = new GoogleAuthProvider();
  } else {
    console.warn("Firebase config missing. Auth disabled.");
  }
} catch (e) {
  console.warn("Firebase init failed:", e);
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  signInWithGoogle: () => Promise<void>;
  sendEmailLink: (email: string) => Promise<void>;
  completeEmailLinkSignIn: () => Promise<boolean>;
  emailLinkSent: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailLinkSent, setEmailLinkSent] = useState(false);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    try {
      const a = auth; // narrow to Auth (non-undefined)
      const unsubscribe = onAuthStateChanged(a, (u: User | null) => {
        setUser(u);
        setAuthEmailForApi(u?.email);
        setLoading(false);
      });
      return unsubscribe;
    } catch (e) {
      console.error("Auth subscription failed:", e);
      setLoading(false);
      return;
    }
  }, []);

  useEffect(() => {
    if (!user?.uid || !user.email) {
      try {
        window.sessionStorage?.removeItem(LOGIN_EVENT_UID_KEY);
      } catch {
        // ignore storage errors
      }
      return;
    }

    try {
      const lastUid = window.sessionStorage?.getItem(LOGIN_EVENT_UID_KEY);
      if (lastUid === user.uid) {
        return;
      }
      window.sessionStorage?.setItem(LOGIN_EVENT_UID_KEY, user.uid);
    } catch {
      // ignore storage errors and still attempt to emit the event once
    }

    void sendFunnelEvent({
      type: "login",
      email: user.email,
      source: "google_auth",
      reason: "firebase_auth_state",
      meta: {
        uid: user.uid,
        display_name: user.displayName || "",
      },
    });
  }, [user?.displayName, user?.email, user?.uid]);

  const sendEmailLink = async (email: string) => {
    if (!auth) return;
    setError(null);
    const appUrl = (import.meta.env.VITE_APP_URL as string | undefined) || window.location.origin;
    try {
      await sendSignInLinkToEmail(auth, email, {
        url: appUrl,
        handleCodeInApp: true,
      });
      try { window.localStorage?.setItem(EMAIL_LINK_KEY, email); } catch { /* ignore */ }
      setEmailLinkSent(true);
    } catch (e) {
      console.error("sendSignInLinkToEmail failed:", e);
      setError("Failed to send sign-in link. Please try again.");
    }
  };

  const completeEmailLinkSignIn = async (): Promise<boolean> => {
    if (!auth || !isSignInWithEmailLink(auth, window.location.href)) return false;
    let email: string | null = null;
    try { email = window.localStorage?.getItem(EMAIL_LINK_KEY) || null; } catch { /* ignore */ }
    if (!email) {
      email = window.prompt("Please enter your email to complete sign-in:");
    }
    if (!email) return false;
    try {
      await signInWithEmailLink(auth, email, window.location.href);
      try { window.localStorage?.removeItem(EMAIL_LINK_KEY); } catch { /* ignore */ }
      // Clean the OTP params from the URL without a reload
      window.history.replaceState({}, document.title, window.location.pathname);
      return true;
    } catch (e) {
      console.error("signInWithEmailLink failed:", e);
      setError("Sign-in link is invalid or expired. Please request a new one.");
      return false;
    }
  };

  const signInWithGoogle = async () => {
    if (!auth || !googleProvider) return;
    setError(null);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (error) {
      console.error("Error signing in with Google", error);
      setError(
        "Failed to sign in. Please verify auth configuration and try again.",
      );
    }
  };

  const logout = async () => {
    if (!auth) return;
    await signOut(auth);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, error, signInWithGoogle, sendEmailLink, completeEmailLinkSignIn, emailLinkSent, logout }}
    >
      {!loading && (
        <>
          {error && (
            <div
              className="fixed top-3 right-3 z-[9999] max-w-sm rounded-md border border-red-400/40 bg-red-500/15 px-3 py-2 text-sm text-red-100 shadow-lg"
              role="status"
              aria-live="polite"
            >
              {error}
            </div>
          )}
          {children}
        </>
      )}
    </AuthContext.Provider>
  );
};
